import React, { createContext, useContext, useState } from 'react';
import snsEnv from '../env/env-sns';
import { filter, get, reject, forEach, attempt } from 'lodash';

type TooltipOptions = {
  message: string;
  link?: string;
  pos?: {
    x: number;
    y: number;
  };
  target?: HTMLElement;
};

type AppData = {
  appData?: any;
  saveAppData: (values: any) => void;
  showToast: (type: string, message: string) => void;
  closeToast: (toastId: string) => void;
  toastsList: { toastId: string; type: string; message: string }[];
  displaySlotChangesMessage: boolean;
  setDisplaySlotChangesMessage: (display: boolean) => void;
  globalBannerMessage: string;
  setGlobalBannerMessage: (message: string) => void;
  showTooltip: (options: TooltipOptions) => void;
  closeTooltip: () => void;
  tooltip: { message: string };
  firstTabBannerMessage: string;
  setFirstTabBannerMessage: (message: string) => void;
  subscribe: (eventName: string, handler: (data: any) => any) => void;
  unsubscribe: (eventName: string, handler: (data: any) => any) => void;
  publish: (eventName: string, data?: any) => void;
  setGlobalAriaLiveMessage: (message: string) => void;
  globalAriaLiveMessage: string;
};

export const appContext = createContext<AppData | null>(null);

const AppProvider = ({ children, appData }) => {
  const [toastsList, setToastsList] = useState<any[]>([]);
  const [displaySlotChangesMessage, setDisplaySlotChangesMessage] = useState(false);
  const [globalBannerMessage, setGlobalBannerMessage] = useState('');
  const [firstTabBannerMessage, setFirstTabBannerMessage] = useState('');
  const [tooltip, setTooltip] = useState<TooltipOptions>({ message: '' });
  const [eventHandlers, setEventHandlers] = useState({});
  const [globalAriaLiveMessage, setGlobalAriaLiveMessage] = useState('');

  const showToast = (type: string, message: string) => {
    // announce new toasts for screen reader users, clearing first to retrigger in case of same message
    setGlobalAriaLiveMessage('');
    setGlobalAriaLiveMessage(`${type}, ${message}`);

    setToastsList((prevToastList) => {
      return [
        ...prevToastList,
        {
          toastId: `toast_${+new Date()}`,
          message: message,
          type: type || 'info',
        },
      ];
    });
  };

  const closeToast = (toastId: string) => {
    setToastsList((prevToastList) => {
      return reject(prevToastList, { toastId: toastId });
    });
  };

  const subscribe = (eventName: string, handler: (data: any) => any) => {
    setEventHandlers({
      ...eventHandlers,
      [eventName]: [...get(eventHandlers, eventName, []), handler],
    });
  };

  const unsubscribe = (eventName: string, handler: (data: any) => any) => {
    setEventHandlers({
      ...eventHandlers,
      [eventName]: filter(get(eventHandlers, eventName), (h) => h !== handler),
    });
  };

  const publish = (eventName: string, data?: any) => {
    forEach(get(eventHandlers, eventName), (handler) => attempt(handler, data));
  };

  if (appData.applicationName === 'Schedule and Save') {
    appData.environmentVariables = snsEnv(appData.banner, appData.env, appData.platform);
  }

  const [currentAppData, setCurrentAppData] = useState(appData);

  const saveAppData = (values) => {
    setCurrentAppData(values);
  };

  const showTooltip = (options: TooltipOptions) => {
    setTooltip({
      message: get(options, 'message'),
      link: get(options, 'link'),
      target: get(options, 'target'),
      pos: get(options, 'pos'),
    });
  };

  const closeTooltip = () => setTooltip({ message: '' });

  return (
    <appContext.Provider
      value={{
        appData: currentAppData,
        saveAppData,
        toastsList,
        showToast,
        closeToast,
        displaySlotChangesMessage,
        setDisplaySlotChangesMessage,
        globalBannerMessage,
        setGlobalBannerMessage,
        firstTabBannerMessage,
        setFirstTabBannerMessage,
        showTooltip,
        closeTooltip,
        tooltip,
        subscribe,
        unsubscribe,
        publish,
        setGlobalAriaLiveMessage,
        globalAriaLiveMessage,
      }}
    >
      {children}
    </appContext.Provider>
  );
};

const useApp = () => {
  return useContext(appContext) as AppData;
};

export { useApp };
export default AppProvider;
